import React, { useEffect, useState } from "react";
import axios from "axios";
import sharedStyles from "../styles/shared.module.css";
import adminStyles from "../styles/AdminPacotesCompras.module.css";

type PacoteCompraDTO = {
    compraId: number;
    status: string;
    pacoteId: number;
    pacoteNome: string;
    motoristaNome: string;
    usuarioNome: string;
    dataReserva: string;
    horaReserva: string;
    adultos: number;
    criancas: number;
    quantidadePessoas: number;
    precoTotal: number;
};

const statusOptions = ["PENDENTE", "PAGO", "CANCELADO", "CONCLUIDO"];

// Mapeamento de cores para os badges
const statusColors: Record<string, string> = {
    PENDENTE: "bg-warning text-dark",
    PAGO: "bg-primary text-white",
    CANCELADO: "bg-danger text-white",
    CONCLUIDO: "bg-success text-white",
};

const AdminPacotesCompras = () => {
    const [compras, setCompras] = useState<PacoteCompraDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const BASE_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const fetchCompras = async () => {
        setLoading(true);
        try {
            const params: any = { page, size: 10 };
            if (statusFilter) params.status = statusFilter;
            if (searchTerm) params.termo = searchTerm;

            const response = await axios.get(`${BASE_URL}/admins/pacote-compras`, {
                headers,
                params,
            });

            const data = response.data;
            if (data.content) {
                setCompras(data.content);
                setTotalPages(data.totalPages);
            } else {
                setCompras(data);
                setTotalPages(1);
            }
        } catch (err) {
            console.error("Erro ao buscar compras:", err);
            setCompras([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (compraId: number, novoStatus: string) => {
        try {
            await axios.patch(
                `${BASE_URL}/admins/pacote-compras/${compraId}/status`,
                {},
                { headers, params: { status: novoStatus } }
            );
            fetchCompras();
        } catch (err) {
            console.error("Erro ao atualizar status:", err);
        }
    };

    useEffect(() => {
        fetchCompras();
    }, [statusFilter, searchTerm, page]);

    return (
        <div className={sharedStyles.container}>
            <h1>Gerenciar Compras de Pacotes</h1>

            <div className={adminStyles.filters}>
                <div className={adminStyles.filterGroup}>
                    <label>ID da Compra</label>
                    <input
                        type="text"
                        placeholder="Digite o ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className={adminStyles.filterGroup}>
                    <label>Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">Todos</option>
                        {statusOptions.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={adminStyles.filterActions}>
                    <button onClick={() => setPage(0)}>Filtrar</button>
                    <button onClick={() => { setSearchTerm(""); setStatusFilter(""); setPage(0); }}>
                        Limpar
                    </button>
                </div>
            </div>

            {loading ? (
                <p>Carregando...</p>
            ) : compras.length === 0 ? (
                <p>Nenhuma compra encontrada.</p>
            ) : (
                <>
                    <table className={adminStyles.table}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Usuário</th>
                                <th>Pacote</th>
                                <th>Motorista</th>
                                <th>Data/Hora</th>
                                <th>Pessoas</th>
                                <th>Preço Total</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {compras.map((compra) => (
                                <tr
                                    key={compra.compraId}
                                    className={
                                        compra.status === "PENDENTE"
                                            ? adminStyles.pendingRow
                                            : compra.status === "CONCLUIDO"
                                                ? adminStyles.completedRow
                                                : ""
                                    }
                                >
                                    <td>{compra.compraId}</td>
                                    <td>{compra.usuarioNome}</td>
                                    <td>
                                        <a
                                            href={`/flight-details/${compra.pacoteId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={adminStyles.linkPacote}
                                        >
                                            {compra.pacoteNome}
                                        </a>
                                    </td>
                                    <td>{compra.motoristaNome}</td>
                                    <td>
                                        {new Date(compra.dataReserva + "T" + compra.horaReserva).toLocaleString()}
                                    </td>
                                    <td>{compra.quantidadePessoas}</td>
                                    <td>
                                        {compra.precoTotal.toLocaleString("pt-BR", {
                                            style: "currency",
                                            currency: "BRL",
                                        })}
                                    </td>
                                    <td>
                                        <span className={`${adminStyles.statusBadge} ${statusColors[compra.status]}`}>
                                            {compra.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={adminStyles.actions}>
                                            <select
                                                value={compra.status}
                                                onChange={(e) =>
                                                    handleStatusChange(compra.compraId, e.target.value)
                                                }
                                                className={adminStyles.statusSelect}
                                            >
                                                {statusOptions.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Paginação */}
                    <div className={adminStyles.pagination}>
                        <button
                            disabled={page === 0}
                            onClick={() => setPage((prev) => prev - 1)}
                        >
                            {"<"}
                        </button>

                        <span className={adminStyles.pageInfo}>
                            Página <strong>{page + 1}</strong> de {totalPages}
                        </span>

                        <button
                            disabled={page + 1 >= totalPages}
                            onClick={() => setPage((prev) => prev + 1)}
                        >
                            {">"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminPacotesCompras;